import { Button, Divider, Filter } from '@vrplatform/components';
import { useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import {
  type MRT_ColumnDef,
  MantineReactTable,
  useLazyTable,
} from '@vrplatform/table';
import type { SelectItem } from '@vrplatform/components';
import { day, formatCurrency, toTitleCase } from '@vrplatform/utils';
import { Box, Center, Group } from '@mantine/core';
import { Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { SalesTaxLiabilityTableFilter } from './SalesTaxLiabilityTableFilter';
import { useSalesTaxLiabilityTableFilter } from './useSalesTaxLiabilityTableFilter';
import {
  type SalesTaxLiabilityTableQueryRow,
  type SalesTaxLiabilityTableQuerySorting,
  useSalesTaxLiabilityTableQuery,
} from './useSalesTaxLiabilityTableQuery';

function useSorting() {
  const [sorting, setSorting] =
    useState<SalesTaxLiabilityTableQuerySorting | null>(null);

  return {
    sorting,
    setSorting,
  };
}

export const SalesTaxLiabilityTable = ({
  onOpenReservation,
}: {
  onOpenReservation?: (reservationId: string) => void;
}) => {
  const [{ currency }] = useTeam();

  const { filter } = useSalesTaxLiabilityTableFilter();

  const { sorting, setSorting } = useSorting();

  const queryData = useSalesTaxLiabilityTableQuery(sorting || 'checkOut');

  const rows = queryData.data?.rows ?? [];

  const columns = useMemo<MRT_ColumnDef<SalesTaxLiabilityTableQueryRow>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        grow: true,
        Cell: (props) => {
          const row = props.row.original;

          if (row.type === 'listing') {
            return <Text fw={500}>{row.name}</Text>;
          }

          if (row.type === 'bookingChannel') {
            return <Text fw={500}>{toTitleCase(row.name)}</Text>;
          }

          if (row.type === 'reservation') {
            return (
              <Box>
                <Text span display="block">
                  {row.guestName}
                </Text>
                {row.confirmationCode && (
                  <Text span display="block" size="xs" c="gray">
                    {row.confirmationCode}
                  </Text>
                )}
              </Box>
            );
          }

          return null;
        },
      },
      {
        header: 'Dates',
        grow: true,
        maxSize: 200,
        Cell: (props) => {
          const row = props.row.original;

          if (row.type === 'reservation') {
            const getFormat = (showYear: boolean) =>
              showYear ? 'DD. MMM. YYYY' : 'DD. MMM';

            const checkIn = day(row.checkIn);
            const checkOut = day(row.checkOut);
            const bookedAt = day(row.bookedAt);
            const cancelledAt = day(row.cancelledAt);

            const checkInDifferentYear = checkIn.year !== checkOut.year;
            const bookedAtDifferentYear = bookedAt.year !== checkIn.year;
            const cancelledAtDifferentYear = cancelledAt.year !== checkIn.year;

            return (
              <Box w={300}>
                <Text span display="block">
                  {`${day(row.checkIn).format(getFormat(checkInDifferentYear))} - ${day(row.checkOut).format(getFormat(true))}`}
                </Text>
                <Text span display="block" size="xs" c="gray">
                  {row.nights} nights
                  {row.bookedAt
                    ? `, on ${day(row.bookedAt).format(getFormat(bookedAtDifferentYear))}`
                    : ''}
                  {row.cancelledAt
                    ? `, cancelled on ${day(row.cancelledAt).format(getFormat(cancelledAtDifferentYear))}`
                    : ''}
                </Text>
              </Box>
            );
          }

          return null;
        },
      },
      {
        header: 'Taxable Income',
        grow: false,
        maxSize: 150,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        footer:
          typeof queryData.data?.taxesIncomeTotal === 'number'
            ? formatCurrency(queryData.data.taxesIncomeTotal / 100, currency)
            : '-',
        mantineTableFooterCellProps: {
          align: 'right',
          sx: (theme) => ({
            paddingRight: theme.spacing.xs,
            fontWeight: 'bold',
          }),
        },
        mantineTableBodyCellProps: (props) => ({
          align: 'right',
          sx: () => ({
            fontWeight: props.row.depth !== 2 ? 'bold' : 'normal',
          }),
        }),
        accessorFn: (row) =>
          formatCurrency(row.taxableIncomeCentTotal / 100, currency),
      },
      {
        header: 'Taxes Collected',
        grow: false,
        maxSize: 150,
        footer:
          typeof queryData.data?.collectableTaxesTotal === 'number'
            ? formatCurrency(
                queryData.data.collectableTaxesTotal / 100,
                currency
              )
            : '-',
        mantineTableFooterCellProps: {
          align: 'right',
          sx: (theme) => ({
            paddingRight: theme.spacing.xs,
            fontWeight: 'bold',
          }),
        },
        mantineTableHeadCellProps: {
          align: 'right',
        },

        mantineTableBodyCellProps: (props) => ({
          align: 'right',
          sx: () => ({
            fontWeight: props.row.depth !== 2 ? 'bold' : 'normal',
          }),
        }),
        accessorFn: (row) =>
          formatCurrency(row.collectableTaxesCentTotal / 100, currency),
      },
    ],
    [
      currency,
      onOpenReservation,
      queryData.data?.taxesIncomeTotal,
      queryData.data?.collectableTaxesTotal,
    ]
  );

  const table = useLazyTable({
    data: {
      error: queryData.error?.message,
      loading: queryData.isLoading,
      rows,
      rowCount: rows.length,
    },
    table: {
      columns,
      hideTopBar: true,
      hidePagination: true,
      enableStickyFooter: true,
      onRowClick: onOpenReservation
        ? {
            handler: (row) => {
              if (row.original.type === 'reservation' && row.original.id) {
                onOpenReservation(row.original.id);
              }
            },
            disabled: (row) => row.original.type !== 'reservation',
          }
        : undefined,
    },
    subRows: {
      getRowCanExpand: (row) => (row.subRows?.length ?? 0) > 0,
      getSubRows: (row) => row.subRows,
      defaultExpanded: true,
      allowExpandingAll: false,
    },
    styles: {
      row: (props, theme) => {
        const depth = 2 - props.row.depth;

        if (depth) {
          return {
            backgroundColor: `${theme.colors.neutral[depth]}!important`,
          };
        }

        return {};
      },
    },
  });

  return (
    <>
      <Group justify="space-between">
        <SalesTaxLiabilityTableFilter />

        <Group>
          <Sort
            sorting={sorting}
            setSorting={(v) => {
              setSorting(v);
            }}
          />
          <Divider orientation="vertical" />

          <Button
            variant="transparent"
            leftIcon="CollapseIcon"
            leftIconSize={14}
            onClick={() => table.toggleAllRowsExpanded()}
          >
            Collapse all
          </Button>
        </Group>
      </Group>
      <Divider mt="sm" />
      {!filter.taxableLineIds.length ? (
        <Center
          mih={300}
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing.md,
          })}
        >
          <Icon
            icon="CalendarEventIcon"
            size={30}
            color={(theme) => theme.colors.gray[6]}
          />
          <Text c="gray">
            Select your reservation financials to calculate the taxable amount
            on each reservation
          </Text>
        </Center>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <MantineReactTable table={table} />
        </Box>
      )}
    </>
  );
};

const Sort = ({
  sorting,
  setSorting,
}: {
  sorting: SalesTaxLiabilityTableQuerySorting | null;
  setSorting: (sorting: SalesTaxLiabilityTableQuerySorting | null) => void;
}) => {
  const options = useMemo<SelectItem<SalesTaxLiabilityTableQuerySorting>[]>(
    () => [
      {
        label: 'Check-In',
        value: 'checkIn',
      },
      {
        label: 'Check-Out',
        value: 'checkOut',
      },
      {
        label: 'Guest Name',
        value: 'guestName',
      },
      {
        label: 'Taxes Collected',
        value: 'collectableTaxesCentTotal',
      },
      {
        label: 'Taxable Income',
        value: 'taxableIncomeCentTotal',
      },
    ],
    []
  );

  const value = options.find((option) => option.value === sorting) ?? null;

  return (
    <Filter.Select
      label="Sort by"
      type="single"
      value={value}
      data={{ options }}
      setValue={(v) => setSorting(v?.value ?? null)}
    />
  );
};
